import { GroupedMatSerNumber, isDefined, MatSerNumber, NotificationIdentifier } from '@evorait/pace-core';
import { createSelector, MemoizedSelector } from '@ngrx/store';
import { FormIdentifier } from '../../types/form-identifier';
import { FormsConstants } from '../../types/forms-constants';
import { FormListUtilities } from '../../utilities/form-list-utilities';
import { FormUtilities } from '../../utilities/form-utilities';
import { NotificationFormListState } from '../state/notification-form-list.state';
import { getPaceFormsState } from './pace-forms.selector';
import { FormList, FormListEntryState } from '../../types/form-list';
import { FormsState } from '../state/pace-forms.state';

export const getFormNotificationLists = createSelector(getPaceFormsState, (state) => state.lists);

export const getListForNotificationFactory = (props: {
    notification: NotificationIdentifier;
}): MemoizedSelector<FormsState, FormList | undefined, (x: NotificationFormListState) => FormList | undefined> =>
    createSelector(
        getFormNotificationLists,
        (notificationLists: NotificationFormListState) =>
            notificationLists[FormListUtilities.generateListIdentifierFromNotification(props.notification)],
    );

export const getFormListEntryForNotificationFactory = (props: {
    formId: FormIdentifier;
    notification: NotificationIdentifier;
}): MemoizedSelector<FormsState, FormListEntryState | undefined, (x: FormList) => FormListEntryState | undefined> =>
    createSelector(getListForNotificationFactory({ notification: props.notification }), (list) => {
        const entries = list?.entries ?? [];
        return entries.find((entry) => entry?.id?.formId === props.formId?.formId);
    });

export const getSelectedLanguageInListForNotificationFactory = (props: {
    notification: NotificationIdentifier;
}): MemoizedSelector<
    FormsState,
    FormsConstants.FORM_LANGUAGES,
    (x: NotificationFormListState) => FormsConstants.FORM_LANGUAGES
> =>
    createSelector(getFormNotificationLists, (notificationLists: NotificationFormListState) => {
        const notificationList =
            notificationLists[FormListUtilities.generateListIdentifierFromNotification(props.notification)];
        return isDefined(notificationList) ? notificationList.selectedLanguage : FormsConstants.FORM_LANGUAGES.English;
    });

export const getFormListAvailableLanguagesForNotificationFactory = (props: {
    notification: NotificationIdentifier;
}): MemoizedSelector<FormsState, FormsConstants.FORM_LANGUAGES[], (x: FormList) => FormsConstants.FORM_LANGUAGES[]> =>
    createSelector(getListForNotificationFactory({ notification: props.notification }), (list) => {
        const entries = list?.entries ?? [];
        const selectedLanguage = list?.selectedLanguage;
        const availableLanguages = entries.map((formListEntry) =>
            FormUtilities.getFormLanguage(formListEntry.id.formId),
        );
        availableLanguages.push(FormsConstants.FORM_LANGUAGES.Multilingual);
        const uniqueLanguages = [...new Set(availableLanguages)].filter((language) => language !== selectedLanguage);
        return uniqueLanguages;
    });

export const getFormLockFromFormListEntry = (props: {
    formId: FormIdentifier;
    notification: NotificationIdentifier;
}): MemoizedSelector<FormsState, string, (x: FormListEntryState) => string> =>
    createSelector(
        getFormListEntryForNotificationFactory({ formId: props.formId, notification: props.notification }),
        (entry) => {
            return isDefined(entry) ? entry.lockedBy : '';
        },
    );

export const getPreviousSubmitFromFormListEntry = (props: {
    formId: FormIdentifier;
    notification: NotificationIdentifier;
}): MemoizedSelector<FormsState, boolean, (x: FormListEntryState) => boolean> =>
    createSelector(
        getFormListEntryForNotificationFactory({ formId: props.formId, notification: props.notification }),
        (entry) => {
            return isDefined(entry) ? entry.previousSubmit : false;
        },
    );

export const getEquipmentSetForNotificationFactory = (props: {
    notification: NotificationIdentifier;
}): MemoizedSelector<FormsState, MatSerNumber[], (x: FormList) => MatSerNumber[]> =>
    createSelector(getListForNotificationFactory({ notification: props.notification }), (list) => {
        return list?.equipmentSet ?? [];
    });

export const getGroupedEquipmentSetForNotificationFactory = (props: {
    notification: NotificationIdentifier;
}): MemoizedSelector<FormsState, GroupedMatSerNumber[], (x: FormList) => GroupedMatSerNumber[]> =>
    createSelector(getListForNotificationFactory({ notification: props.notification }), (list) => {
        return (
            list?.equipmentSet.reduce((acc, equipmentSet) => {
                const existingEquipment = acc.find((value) => value.matnr === equipmentSet.matnr);
                if (isDefined(existingEquipment)) {
                    existingEquipment.sernr.push(equipmentSet.sernr);
                    return acc;
                }

                return [
                    ...acc,
                    {
                        matnr: equipmentSet.matnr,
                        sernr: [equipmentSet.sernr],
                        description: equipmentSet.description,
                    },
                ];
            }, [] as GroupedMatSerNumber[]) ?? []
        );
    });

export const getIsNotificationCompletedFactory = (props: {
    notification: NotificationIdentifier;
}): MemoizedSelector<FormsState, boolean, (x: FormList) => boolean> =>
    createSelector(getListForNotificationFactory({ notification: props.notification }), (list) => {
        const entries = list?.entries ?? [];
        return entries.reduce((acc, curr) => {
            if (!acc) {
                return acc;
            }
            return FormListUtilities.isFormCompleted(curr.submitStatus);
        }, true);
    });

export const getIsNotificationStartedFactory = (props: {
    notification: NotificationIdentifier;
}): MemoizedSelector<FormsState, boolean, (x: FormList) => boolean> =>
    createSelector(getListForNotificationFactory({ notification: props.notification }), (list) => {
        const entries = list?.entries ?? [];
        return entries.reduce((acc, curr) => {
            if (!acc) {
                return acc;
            }
            return FormListUtilities.isFormEdited(curr.submitStatus);
        }, true);
    });

export const getFormIdsForNotificationFactory = (props: {
    notification: NotificationIdentifier;
}): MemoizedSelector<FormsState, string[], (x: FormList) => string[]> =>
    createSelector(getListForNotificationFactory({ notification: props.notification }), (list) => {
        const entries = list?.entries ?? [];
        return entries.map((entry) => entry.id.formId);
    });

export const getFormIdsAndStatusForNotificationFactory = (props: {
    notification: NotificationIdentifier;
}): MemoizedSelector<FormsState, string[], (x: FormList) => string[]> =>
    createSelector(getListForNotificationFactory({ notification: props.notification }), (list) => {
        const entries = list?.entries ?? [];
        return entries.map((entry) => `${entry.id.formId}:${entry.submitStatus}`);
    });
